import { useCallback, useRef, useState, useEffect } from 'react';
import { callOpenAITTS, callElevenLabsTTS } from '../../services/cloudFunctions';

interface UseTextToSpeechReturn {
  speak: (text: string) => Promise<void>;
  stop: () => void;
  isSpeaking: boolean;
  isSupported: boolean;
}

/**
 * Preprocesses text for more natural TTS intonation
 * Fixes common issues like:
 * - "Ik ben [naam]" sounding like a question (should be declarative)
 * - Missing pauses between sentences and after commas
 * - Questions not ending with proper punctuation
 * - Creates natural breathing pauses after introductions
 */
const preprocessTextForNaturalSpeech = (text: string): string => {
  let processed = text;

  // 1. Fix "Ik ben/heet [name]" statements to sound declarative not questioning
  // Add emphasis marker before name to force falling intonation
  processed = processed.replace(
    /(Mijn naam is|Ik ben|Ik heet)\s+([A-Z][a-z]+)/g,
    '$1 $2'
  );

  // 2. Ensure proper spacing after punctuation
  processed = processed.replace(/([.!?,:;])([A-Za-z])/g, '$1 $2');

  // 3. Add natural pauses between sentences (double space = longer pause)
  processed = processed.replace(/\.\s+/g, '.  ');
  processed = processed.replace(/\?\s+/g, '?  ');
  processed = processed.replace(/!\s+/g, '!  ');
  
  // 4. Add pause after commas (especially important after introductions)
  processed = processed.replace(/,\s+/g, ',  ');

  // 5. Ensure questions end with proper punctuation for rising intonation
  const questionWords = ['wie', 'wat', 'waar', 'wanneer', 'waarom', 'hoe', 'welke', 'welk', 'kan', 'wil', 'mag'];
  const sentences = processed.split(/([.!?]+\s+)/);
  
  processed = sentences.map(sentence => {
    const trimmed = sentence.trim().toLowerCase();
    const startsWithQuestion = questionWords.some(word => 
      trimmed.startsWith(word + ' ')
    );
    
    if (startsWithQuestion && !sentence.includes('?')) {
      return sentence.replace(/[.!]*$/, '?');
    }
    return sentence;
  }).join('');

  // 6. Normalize common Dutch phrases
  processed = processed
    .replace(/\bok[é|e]\b/gi, 'oké')
    .replace(/\bja\b\s+/gi, 'ja,  ') // Add pause after 'ja'
    .replace(/\bnee\b\s+/gi, 'nee,  '); // Add pause after 'nee'

  // 7. Split very long sentences at natural break points
  if (processed.length > 150) {
    processed = processed.replace(
      /(.{80,150}[,;])\s+/g, 
      '$1.  '
    );
  }

  // 8. Ensure proper ending punctuation for declarative tone
  if (!/[.!?]$/.test(processed.trim())) {
    processed = processed.trim() + '.';
  }

  // 9. Clean up multiple spaces (but keep double spaces after punctuation)
  processed = processed.replace(/([^.!?,])\s{2,}/g, '$1 ').trim();

  return processed;
};

export const useTextToSpeech = (): UseTextToSpeechReturn => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const isSupported = true; // OpenAI TTS is always "supported" if API key exists
  const abortControllerRef = useRef<AbortController | null>(null);

  const stop = useCallback(() => {
    // Abort any pending fetch
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    // Stop audio playback
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    
    setIsSpeaking(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stop();
    };
  }, [stop]);

  const speak = useCallback(async (text: string): Promise<void> => {
    if (!text.trim()) return Promise.resolve();

    stop();
    setIsSpeaking(true);

    const voiceId = process.env.REACT_APP_ELEVENLABS_VOICE_ID || '21m00Tcm4TlvDq8ikWAM';
    const processedText = preprocessTextForNaturalSpeech(text);

    return new Promise(async (resolve) => {
      try {
        abortControllerRef.current = new AbortController();
        let audioBuffer: ArrayBuffer | null = null;
        let usedProvider = 'none';

        // Primair: OpenAI TTS via Cloud Function (geen API key in browser)
        try {
          audioBuffer = await callOpenAITTS({
            text: processedText,
            voice: 'nova',
            model: 'tts-1',
            speed: 1.15,
          });
          usedProvider = 'openai';
        } catch (err) {
          console.warn('OpenAI TTS mislukt, probeer ElevenLabs...', err);
        }

        // Fallback: ElevenLabs via Cloud Function
        if (!audioBuffer) {
          try {
            audioBuffer = await callElevenLabsTTS({
              text: processedText,
              voiceId,
              modelId: 'eleven_multilingual_v2',
              stability: 0.5,
              similarityBoost: 0.75,
              style: 0.5,
              useSpeakerBoost: true,
            });
            usedProvider = 'elevenlabs';
          } catch (err) {
            console.warn('ElevenLabs TTS ook mislukt:', err);
          }
        }

        if (!audioBuffer) {
          console.error('Geen TTS provider beschikbaar');
          setIsSpeaking(false);
          resolve();
          return;
        }

        const audioBlob = new Blob([audioBuffer], { type: 'audio/mpeg' });

        // Play the audio
        const audioUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(audioUrl);
        audioRef.current = audio;

        // Log which provider was used (helpful for debugging)
        console.log(`🔊 Playing audio from: ${usedProvider}`);

        // isSpeaking already set to true at start of speak()
        audio.onplaying = () => {
          console.log('▶️ Audio playback started');
        };

        audio.onended = () => {
          URL.revokeObjectURL(audioUrl);
          audioRef.current = null;
          setIsSpeaking(false);
          resolve();
        };

        audio.onerror = () => {
          URL.revokeObjectURL(audioUrl);
          audioRef.current = null;
          setIsSpeaking(false);
          resolve();
        };

        // Handle abort
        audio.onabort = () => {
          URL.revokeObjectURL(audioUrl);
          audioRef.current = null;
          setIsSpeaking(false);
          resolve();
        };

        try {
          await audio.play();
        } catch (playError: any) {
          if (playError.name === 'NotAllowedError') {
            // Browser blocked autoplay (no prior user gesture). Wait for
            // the first user interaction and then replay the greeting.
            console.warn('⏸️ Audio autoplay blocked – queuing playback on next user interaction');
            await new Promise<void>((resolveRetry) => {
              const cleanup = () => {
                document.removeEventListener('click', retryOnInteraction);
                document.removeEventListener('keydown', retryOnInteraction);
                document.removeEventListener('touchstart', retryOnInteraction);
              };
              const retryOnInteraction = async () => {
                cleanup();
                // Abort if audio was stopped (e.g. overlay closed) before user interacted
                if (audioRef.current !== audio) {
                  resolveRetry();
                  return;
                }
                try {
                  await audio.play();
                } catch {
                  URL.revokeObjectURL(audioUrl);
                  audioRef.current = null;
                  setIsSpeaking(false);
                  resolveRetry();
                }
              };
              document.addEventListener('click', retryOnInteraction, { once: true });
              document.addEventListener('keydown', retryOnInteraction, { once: true });
              document.addEventListener('touchstart', retryOnInteraction, { once: true });
            });
          } else {
            throw playError;
          }
        }
        
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          console.error('❌ TTS error:', error);
        }
        setIsSpeaking(false);
        resolve();
      }
    });
  }, [stop]);

  return {
    speak,
    stop,
    isSpeaking,
    isSupported
  };
};
